import { useEffect, useRef, useState } from "react";

const BoardNameModal = ({
  isOpen,
  mode = "create",
  initialName = "",
  isSubmitting = false,
  errorMessage = "",
  onClose,
  onSubmit,
}) => {
  const [boardName, setBoardName] = useState("");
  const inputRef = useRef(null);

  const isEditMode = mode === "edit";

  useEffect(() => {
    if (!isOpen) return;

    setBoardName(initialName || "");

    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
  }, [isOpen, initialName]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const trimmedName = boardName.trim();

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!trimmedName || isSubmitting) return;

    onSubmit?.(trimmedName);
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close board modal"
        disabled={isSubmitting}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/55"
      />

      <form
        onSubmit={handleSubmit}
        className="
          relative z-[121] w-full max-w-[430px] rounded-[6px] bg-white
          px-6 pb-6 pt-5 shadow-[0_20px_70px_rgba(0,0,0,0.25)]
        "
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="mb-2 font-spaceMono text-[10px] uppercase tracking-[0.18em] text-[#777]">
              {isEditMode ? "Edit Board" : "Create Board"}
            </p>

            <h2 className="font-inter text-[24px] font-medium leading-none text-[#252525]">
              {isEditMode ? "Rename board" : "New board"}
            </h2>
          </div>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            className="
              flex h-[30px] w-[30px] shrink-0 items-center justify-center
              rounded-[4px] bg-[#F6F6F6] font-inter text-[14px] text-black
              transition hover:bg-[#ECECEC] disabled:cursor-not-allowed disabled:opacity-60
            "
          >
            ×
          </button>
        </div>

        <label className="block">
          <span className="mb-2 block font-inter text-[13px] font-medium text-[#313131]">
            Board name
          </span>

          <input
            ref={inputRef}
            value={boardName}
            onChange={(event) => setBoardName(event.target.value)}
            placeholder="Example: Going Out"
            disabled={isSubmitting}
            maxLength={80}
            className="
              h-[44px] w-full rounded-[5px] border border-[#D8D8D8]
              bg-white px-4 font-inter text-[14px] text-black outline-none
              transition placeholder:text-[#A0A0A0]
              focus:border-[#313131]
              disabled:cursor-not-allowed disabled:bg-[#F7F7F7]
            "
          />
        </label>

        {errorMessage ? (
          <p className="mt-3 font-inter text-[12px] text-[#D43F3F]">
            {errorMessage}
          </p>
        ) : null}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            className="
              h-[38px] rounded-[5px] border border-[#D9D9D9] bg-white px-5
              font-inter text-[13px] text-black transition hover:bg-[#F6F6F6]
              disabled:cursor-not-allowed disabled:opacity-60
            "
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={!trimmedName || isSubmitting}
            className="
              h-[38px] min-w-[118px] rounded-[5px] bg-[#8F333B] px-5
              font-inter text-[13px] font-medium text-white transition
              hover:opacity-[95%]
              disabled:cursor-not-allowed disabled:opacity-[80%]
            "
          >
            {isSubmitting
              ? isEditMode
                ? "Saving..."
                : "Creating..."
              : isEditMode
                ? "Save changes"
                : "Create board"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BoardNameModal;
